import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AppSettingsService } from '../config/config-service';
import { TokenIdService } from './token-id.service';
import {  CommonService } from './common.service';

@Injectable({
  providedIn: 'root'
})
export class AdminService {
    apiUrl:string | undefined;

  constructor(
    private http: CommonService,
    private appSettingsService: AppSettingsService,
    private tokenIdService: TokenIdService
  ) {
    this.apiUrl = this.appSettingsService.getSettings().apiEndPoint || '';
  }

  login(email: string, password: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/api/admin/login`, { email, password }).pipe(
      map(response => {
        if (response?.data?.tokens?.authToken) {
          localStorage.setItem('authToken', response.data.tokens.authToken);
          localStorage.setItem('refreshToken', response.data.tokens.refreshToken);
        }
        return response;
      }),
      catchError(this.handleError)
    );
  }

  getProfile(token: string, id: any) {
    return this.http.get<any>(`${this.apiUrl}/api/admin/getAdmin/${id}`).pipe(
      map(response => response.data?.admin || response.data),
      catchError(this.handleError)
    );
  }

  getAdminIdFromToken(token?: string): string | null {
    return this.tokenIdService.getAdminIdFromToken(token);
  }

  getUsers(page: number = 1, limit: number = 10, search: string = '') {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    if (search) {
      params = params.set('search', search);
    }

    return this.http.get<any>(`${this.apiUrl}/api/auth/users`, { params }).pipe(
      map(response => response.data),
      catchError(this.handleError)
    );
  }

  deleteUser(userId: string): Observable<any> {
    return this.http.delete<any>(`${this.apiUrl}/api/admin/users/${userId}`).pipe(
      map(response => response),
      catchError(this.handleError)
    );
  }

  getPortfolios(page: number = 1, limit: number = 10) {
    return this.http.get<any>(`${this.apiUrl}/api/portfolios/getAllPortfolios?page=${page}&limit=${limit}`).pipe(
      map(response => response.data),
      catchError(this.handleError)
    );
  }

  createPortfolio(portfolio: any) {
    return this.http.post<any>(`${this.apiUrl}/api/portfolios/create`, portfolio).pipe(
      map(response => response.data),
      catchError(this.handleError)
    );
  }

  // Get pre-signed PUT URL for S3 upload
  getPresignedUrl(params: { fileName: string; contentType: string; size: number }): Observable<{ uploadUrl: string; url: string; key: string }> {
    return this.http.post<{ uploadUrl: string; url: string; key: string }>(`${this.apiUrl}/api/uploads/presign`, params
    ).pipe(catchError(this.handleError));
  }

  updatePortfolio(id: string, portfolio: any) {
    return this.http.put<any>(`${this.apiUrl}/api/portfolios/update/${id}`, portfolio).pipe(
      map(response => response.data),
      catchError(this.handleError)
    );
  }

  deletePortfolio(id: string) {
    return this.http.delete<any>(`${this.apiUrl}/api/portfolios/delete/${id}`).pipe(
      map(response => response.data),
      catchError(this.handleError)
    );
  }

  private handleError(error: any) {
    let errorMessage = 'An error occurred. Please try again.';
    if (error instanceof HttpErrorResponse) {
      if (error.error?.message) {
        errorMessage = error.error.message;
      } else if (error.status === 401) {
        errorMessage = 'Invalid credentials. Please check your email and password.';
      } else if (error.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      }
    }
    return throwError(() => new Error(errorMessage));
  }
  getAllTransactions(page: number = 1, limit: number = 20, search: string = '', status: string = 'all', startDate?: string, endDate?: string): Observable<any> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    if (search) {
      params = params.set('search', search);
    }

    if (status && status !== 'all') {
      params = params.set('status', status);
    }

    if (startDate && endDate) {
      params = params.set('startDate', startDate);
      params = params.set('endDate', endDate);
    }

    return this.http.get(`${this.apiUrl}/api/transactions/getAllTransactions`, { params });
  }

  updateTransactionStatus(transactionId: string, status: string): Observable<any> {
    return this.http.put(`${this.apiUrl}/api/transactions/updateStatus/${transactionId}`, { status });
  }
  processTransaction(transactionId: string, requestData: any): Observable<any> {
    return this.http.put(`${this.apiUrl}/api/transactions/processTransaction/${transactionId}`, requestData);
  }
  deleteTransaction(transactionId: string): Observable<any> {
    return this.http.delete(`${this.apiUrl}/api/transactions/deleteTransaction/${transactionId}`).pipe(
      map((response: any) => response),
      catchError(this.handleError)
    );
  }
  publishPortfolio(id: string, actualRoiPercentage: number) {
    const body = { id, actualRoiPercentage };
    return this.http.post<any>(`${this.apiUrl}/api/portfolios/publishPortfolio`, body).pipe(
      map(response => response.data),
      catchError(this.handleError)
    );
  }

  getUserDetailsForAdmin(userId: any): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/api/auth/users/${userId}/details`).pipe(
      map(response => response),
      catchError(this.handleError)
    );
  }

  getInvestmentConsultations(page: number = 1, limit: number = 10, search: string = ''): Observable<any> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    if (search) {
      params = params.set('search', search);
    }

    return this.http.get<any>(`${this.apiUrl}/api/investmentConsultations/getAllConsultations`, { params });
  }

  resetUserPassword(userId: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/api/auth/users/${userId}/reset-password`, {}).pipe(
      map(response => response),
      catchError(this.handleError)
    );
  }

  getArchivedUsers(page: number = 1, limit: number = 10, search: string = ''): Observable<any> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    if (search) {
      params = params.set('search', search);
    }

    return this.http.get<any>(`${this.apiUrl}/api/auth/archived-users`, { params }).pipe(
      map(response => response.data),
      catchError(this.handleError)
    );
  }
}
